import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Loader2, MessageCircle, HelpCircle, Send } from "lucide-react";

const Contact = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const faqCategories = [
    {
      key: "gettingStarted",
      items: ["howToStart", "createScript", "sceneTypes", "trial"],
    },
    {
      key: "subscription",
      items: ["plans", "upgrade", "cancel", "refund"],
    },
    {
      key: "features",
      items: ["mirrorMode", "customization", "export", "offline"],
    },
    {
      key: "technical",
      items: ["browsers", "mobile", "sync", "performance"],
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !subject || !message) {
      toast({
        title: t("contact.error"),
        description: t("contact.fillAllFields"),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const { error } = await supabase.from("support_tickets").insert({
        user_id: user?.id || null,
        name,
        email,
        subject,
        message,
      });

      if (error) throw error;

      toast({
        title: t("contact.success"),
        description: t("contact.successDesc"),
      });

      // Reset form
      setName("");
      setSubject("");
      setMessage("");
      if (!user) setEmail("");
    } catch (error) {
      console.error("Submit error:", error);
      toast({
        title: t("contact.error"),
        description: t("contact.submitError"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LandingHeader />
      
      <main className="flex-1 py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold mb-4">{t("contact.title")}</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("contact.subtitle")}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* FAQ Section */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <HelpCircle className="w-6 h-6 text-primary" />
                <h2 className="text-2xl font-semibold">{t("contact.faq.title")}</h2>
              </div>

              <div className="space-y-6">
                {faqCategories.map((category) => (
                  <Card key={category.key}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">
                        {t(`contact.faq.categories.${category.key}.title`)}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Accordion type="single" collapsible>
                        {category.items.map((item, index) => (
                          <AccordionItem key={item} value={`${category.key}-${index}`}>
                            <AccordionTrigger className="text-left">
                              {t(`contact.faq.categories.${category.key}.items.${item}.q`)}
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground">
                              {t(`contact.faq.categories.${category.key}.items.${item}.a`)}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <MessageCircle className="w-6 h-6 text-primary" />
                <h2 className="text-2xl font-semibold">{t("contact.form.title")}</h2>
              </div>

              <Card>
                <CardHeader>
                  <CardDescription>{t("contact.form.subtitle")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t("contact.form.name")}</Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("contact.form.namePlaceholder")}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">{t("contact.form.email")}</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("contact.form.emailPlaceholder")}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject">{t("contact.form.subject")}</Label>
                      <Select value={subject} onValueChange={setSubject} required>
                        <SelectTrigger>
                          <SelectValue placeholder={t("contact.form.subjectPlaceholder")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">{t("contact.form.subjects.general")}</SelectItem>
                          <SelectItem value="billing">{t("contact.form.subjects.billing")}</SelectItem>
                          <SelectItem value="technical">{t("contact.form.subjects.technical")}</SelectItem>
                          <SelectItem value="feature">{t("contact.form.subjects.feature")}</SelectItem>
                          <SelectItem value="other">{t("contact.form.subjects.other")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">{t("contact.form.message")}</Label>
                      <Textarea
                        id="message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t("contact.form.messagePlaceholder")}
                        rows={5}
                        required
                      />
                    </div>

                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t("contact.form.sending")}
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {t("contact.form.send")}
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
};

export default Contact;
